import './CashFlowDashboard.css';

interface BalanceSheetMetrics {
    totalAssets?: {
        value: number;
        formattedValue: string;
        label: string;
    };
    totalLiabilities?: {
        value: number;
        formattedValue: string;
        label: string;
    };
    totalEquity?: {
        value: number;
        formattedValue: string;
        label: string;
    };
}

interface DashboardHeaderBalanceSheetProps {
    keyMetrics: BalanceSheetMetrics | null;
}

const DashboardHeaderBalanceSheet: React.FC<DashboardHeaderBalanceSheetProps> = ({ keyMetrics }) => {
    if (!keyMetrics) return null;

    const formatTitle = (key: string) => {
        const titleMappings: { [key: string]: string } = {
            'totalAssets': 'Total Assets',
            'totalLiabilities': 'Total Liabilities',
            'totalEquity': 'Total Equity'
        };
        return titleMappings[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };

    const getMetricContext = (key: string, value: number) => {
        const contexts: { [key: string]: { positive: string; negative: string } } = {
            'totalAssets': {
                positive: 'Strong asset base',
                negative: 'Declining assets'
            },
            'totalLiabilities': {
                positive: 'Manageable debt levels',
                negative: 'High debt burden'
            },
            'totalEquity': {
                positive: 'Healthy equity position',
                negative: 'Negative equity'
            }
        };
        const context = contexts[key];
        return context ? (value >= 0 ? context.positive : context.negative) : '';
    };

    const MetricCard = ({ title, data, metricKey, className = '' }: { title: string; data: any; metricKey: string; className?: string }) => {
        const numValue = data?.value || 0;
        const isPositive = numValue >= 0;
        const context = getMetricContext(metricKey, numValue);
        
        return (
            <div className={`metric-card tab-card border tab-card-border rounded-md p-2 ${isPositive ? 'positive' : 'negative'} ${className}`}>
                <h3 className="text-xs text-slate-400 mb-0.5">{title}</h3>
                <div className="flex items-baseline justify-between">
                    <span className="text-sm font-medium" style={{ color: isPositive ? '#10B981' : '#EF4444' }}>
                        {data?.formattedValue || 'N/A'}
                    </span>
                    <span className="ml-2 text-xs" style={{ color: isPositive ? '#10B981' : '#EF4444' }}>
                        {isPositive ? '↗' : '↘'}
                    </span>
                </div>
                {context && (
                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">
                        {context}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="dashboard-header mt-3" style={{ marginBottom: '0px' }}>
            <div className="grid grid-cols-3 gap-2 key-metrics" style={{ minHeight: '60px' }}>
                {Object.entries(keyMetrics).map(([key, value]: [string, any]) => (
                    <MetricCard
                        key={key}
                        title={formatTitle(key)}
                        data={value}
                        metricKey={key}
                    />
                ))}
            </div>
        </div>
    );
};

export default DashboardHeaderBalanceSheet;
